import React, { useMemo } from "react";
import { twMerge } from "tailwind-merge";
import ButtonGroupButton, { type ButtonProps } from "./Button";
import isEqual from "lodash.isequal";

export type ButtonGroupContextType<T> = {
  selectedOption: T;
  onOptionChange: (selectedOption: T) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
};

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export const ButtonGroupContext = React.createContext<ButtonGroupContextType<any> | undefined>(undefined);

type ButtonGroupProps<T> = {
  selectedOption: T;
  onOptionChange: (selectedOption: T) => void;
  className?: string;
  ariaLabel?: string;
  children: React.ReactNode;
};

const ButtonGroup = <T = string>({
  selectedOption,
  onOptionChange,
  className = "",
  ariaLabel = "Button group",
  children,
}: ButtonGroupProps<T>) => {
  const options = useMemo(
    () =>
      React.Children.toArray(children)
        .filter(React.isValidElement)
        .map((child) => (child as React.ReactElement<ButtonProps<T>>).props.value),
    [children],
  );

  const handleButtonKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    switch (event.key) {
      case "Enter":
        onOptionChange(selectedOption);
        event.preventDefault();
        break;
      case "ArrowRight":
        {
          const currentIndex = options.findIndex((option) => isEqual(option, selectedOption));
          const nextIndex = (currentIndex + 1) % options.length;
          onOptionChange(options[nextIndex]);
        }
        break;
      case "ArrowLeft":
        {
          const currentIndex = options.findIndex((option) => isEqual(option, selectedOption));
          const prevIndex = (currentIndex - 1 + options.length) % options.length;
          onOptionChange(options[prevIndex]);
        }
        break;
    }
  };

  return (
    <ButtonGroupContext.Provider
      value={{
        selectedOption,
        onOptionChange,
        onKeyDown: handleButtonKeyDown,
      }}
    >
      <div
        className={twMerge("flex gap-2 p-1 w-full bg-gray-100 rounded-md", className)}
        // biome-ignore lint/a11y/useSemanticElements: <explanation>
        role="group"
        aria-label={ariaLabel}
      >
        {children}
      </div>
    </ButtonGroupContext.Provider>
  );
};

ButtonGroup.Button = ButtonGroupButton;

export default ButtonGroup;
